'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import { Button, Badge, Card, CardContent, Picture, Text, Heading, Progress } from '../../components/index.js'

import type { ButtonFunctionType, CopyConfig } from './types.js'
import type { BadgeProps } from '../../components/badge.js'

/**
 * 超卖时库存条显示的最小百分比
 * 防止超卖时库存条显示为满格（100%）或完全空（0%）
 */
const OVERSELLING_STOCK_PERCENTAGE = 5

/**
 * 语义化类名
 */
export type ActiveShelfSemanticName =
  | 'root'
  | 'title'
  | 'description'
  | 'productCard'
  | 'productTitle'
  | 'productDescription'
  | 'productPriceLabel'
  | 'productPrice'
  | 'buttonGroup'
  | 'secondaryButton'
  | 'primaryButton'
  | 'stockTrack'
  | 'stockBar'

/**
 * 库存展示模式
 */
export type StockDisplayMode = 'always' | 'never' | 'below-quantity' | 'below-percentage'

/**
 * 基础产品数据接口（来自外部数据源）
 */
export interface BaseProductData {
  sku: string
  name: string
  image: string
  value: string
  handle: string
  shopify_id: string
  custom_name?: string
  custom_description?: string
}

/**
 * Shopify 变体数据接口
 */
export interface VariantData {
  /**
   * 产品描述
   */
  description?: string
  /**
   * 变体id
   */
  variantId: string

  /**
   * 产品链接, 点击图片的时候跳转到该链接
   */
  listingLink: string
  /**
   * 原价
   */
  originalPrice: string
  /**
   * 现价
   */
  price: string
  /**
   * 价格上面展示的标签文本
   */
  priceLabel?: string
  /**
   * 总库存
   */
  totalInventory: number
  /**
   * 可用库存数量
   */
  quantityAvailable: number
  /**
   * 是否可售
   */
  availableForSale: boolean

  /**
   * tags 列表(会员价，折扣，new, hot等标签信息)
   */
  tags: Array<
    | {
        label: string
        variant: BadgeProps['variant']
        promotionalType?: BadgeProps['promotionalType']
      }
    | React.ReactNode
  >
  /**
   * 通用折扣数据，包括折扣title, 类型和值
   */
  coupon: any
}

/**
 * 完整的产品卡片数据接口
 */
export interface ProductCardData extends BaseProductData, VariantData {}

/**
 * 产品卡片组件 Props
 */
export interface ProductCardProps {
  /** 产品数据 */
  product: ProductCardData
  /** 样式类名 */
  className?: string
  /** 是否展示标签系统 */
  showTags?: boolean
  /** 库存展示模式 */
  stockDisplayMode?: StockDisplayMode
  /** 库存阈值（当模式为 below-quantity 或 below-percentage 时使用） */
  stockThresholdValue?: number
  /** 点击了解更多回调 */
  onLearnMore?: (product: ProductCardData) => void
  /** 点击立即购买回调 */
  onShopNow?: (product: ProductCardData) => void
  /** 点击加入购物车回调 */
  onAddToCart?: (product: ProductCardData) => void
  /** 语义化类名 */
  classNames?: Partial<Record<ActiveShelfSemanticName, string>>
  /** 了解更多按钮文本 */
  secondaryButtonText?: string
  /** 了解更多按钮功能 */
  secondaryButtonFun?: ButtonFunctionType
  /** 立即购买按钮文本 */
  primaryButtonText?: string
  /** 立即购买按钮功能 */
  primaryButtonFun?: ButtonFunctionType
  /** 是否展示原价（删除线价格） */
  showOriginalPrice?: boolean
  /** 文案配置 */
  copy?: CopyConfig
  onProductImageClick?: (product: ProductCardData) => void
}

/**
 * 判断是否应该显示库存信息
 * @param stockDisplayMode 库存展示模式
 * @param stockThresholdValue 库存阈值（当模式为 below-quantity 或 below-percentage 时使用）
 * @param quantityAvailable 可用库存数量
 * @param totalInventory 总库存
 * @returns 是否显示库存信息
 */
function shouldShowStock(
  stockDisplayMode: StockDisplayMode | undefined,
  stockThresholdValue: number | undefined,
  quantityAvailable: number,
  totalInventory: number
): boolean {
  // 未配置时默认显示
  if (stockDisplayMode === undefined) {
    return true
  }

  // 根据展示模式判断
  switch (stockDisplayMode) {
    case 'always':
      // 总是显示
      return true

    case 'never':
      // 永不显示
      return false

    case 'below-quantity':
      // 当库存数量低于阈值时显示
      if (stockThresholdValue === undefined) {
        return true // 没有设置阈值时默认显示
      }

      return quantityAvailable <= stockThresholdValue

    case 'below-percentage':
      // 当库存百分比低于阈值时显示
      if (stockThresholdValue === undefined) {
        return true // 没有设置阈值时默认显示
      }
      return (quantityAvailable / totalInventory) * 100 <= stockThresholdValue

    default:
      // 未知模式时默认显示
      return true
  }
}

/**
 * 产品卡片组件
 */
const ProductCard = React.forwardRef<HTMLDivElement, ProductCardProps>(
  (
    {
      product,
      className,
      showTags = true,
      stockDisplayMode,
      stockThresholdValue,
      onLearnMore,
      onShopNow,
      onAddToCart,
      classNames,
      secondaryButtonText,
      secondaryButtonFun,
      primaryButtonText,
      primaryButtonFun,
      showOriginalPrice,
      copy,
      onProductImageClick,
    },
    ref
  ) => {
    const [primaryLoading, setPrimaryLoading] = React.useState(false)
    const [secondaryLoading, setSecondaryLoading] = React.useState(false)

    const stockPercentage = React.useMemo(() => {
      if (product?.availableForSale && product?.quantityAvailable <= 0) {
        return OVERSELLING_STOCK_PERCENTAGE
      }
      // 不可售时显示0%
      if (!product?.availableForSale) {
        return 0
      }
      return ((product?.quantityAvailable ?? 0) / (product?.totalInventory ?? 1)) * 100
    }, [product?.availableForSale, product?.quantityAvailable, product?.totalInventory])

    // 根据按钮功能类型调用相应的回调函数
    const handleButtonClick = async (buttonFun?: ButtonFunctionType, buttonType?: 'primary' | 'secondary') => {
      if (!buttonFun) return

      const setLoading = buttonType === 'primary' ? setPrimaryLoading : setSecondaryLoading
      setLoading(true)

      try {
        switch (buttonFun) {
          case 'buyNow':
            await onShopNow?.(product)
            break
          case 'addCart':
            await onAddToCart?.(product)
            break
          case 'learnMore':
            await onLearnMore?.(product)
            break
          default:
            break
        }
      } finally {
        setLoading(false)
      }
    }

    const overselling = product.availableForSale && product.quantityAvailable <= 0

    return (
      <Card
        ref={ref}
        className={cn(
          'bg-container-primary hover:bg-container-secondary-0 flex h-full flex-col overflow-hidden border-none',
          className,
          classNames?.productCard
        )}
      >
        <CardContent className="desktop:p-6 relative flex flex-1 flex-col justify-between p-4">
          {/* 标签 */}
          <div className="lg-desktop:h-[28px] flex h-[24px] gap-1">
            {showTags &&
              product.tags?.map((tag: any, index) =>
                (tag as any).label ? (
                  <Badge
                    key={index}
                    size="sm"
                    variant={(tag.variant as any) || 'outline'}
                    className={cn(tag.variant === 'promotional' ? 'ml-2' : '')}
                    promotionalType={tag.promotionalType}
                  >
                    {tag.label}
                  </Badge>
                ) : (
                  <React.Fragment key={index}>{tag}</React.Fragment>
                )
              )}
          </div>

          {/* 产品图区域 */}
          <div className="product-image-wrapper lg-desktop:size-[196px] mx-auto size-[138px] cursor-pointer overflow-hidden">
            <a
              onClick={() => onProductImageClick?.(product)}
              {...(!onProductImageClick && {
                href: product?.listingLink,
              })}
              rel="noreferrer"
            >
              <Picture
                source={product.image}
                alt={product.name}
                className="aspect-square size-full object-contain"
                imgClassName="w-full h-full object-contain"
              />
            </a>
          </div>

          {/* 内容区域 */}
          <div className="flex h-full flex-1 grow flex-col justify-between">
            {/* 产品信息 */}
            <div className="mb-4">
              <Heading
                as="h3"
                size={2}
                className={cn('text-info-primary mb-2 line-clamp-2 min-h-[2.4em]', classNames?.productTitle)}
                html={product.custom_name || product.name}
              />
              {product?.description && (
                <Text
                  size={2}
                  className={cn(
                    'text-info-secondary desktop:line-clamp-2 desktop:min-h-[2.4em] desktop:text-[16px] lg-desktop:text-[18px] line-clamp-2 min-h-[2.4em] text-[14px]',
                    classNames?.productDescription
                  )}
                  html={product.custom_description || product.description}
                />
              )}
            </div>

            {/** 价格标签 */}

            {product?.priceLabel && product?.availableForSale && (
              <Text
                size={4}
                className={cn(
                  'text-marketing-1 desktop:text-[16px] lg-desktop:text-[18px] mt-2 text-[14px]',
                  classNames?.productPriceLabel
                )}
              >
                {product.priceLabel}
              </Text>
            )}

            {/* 价格区域 */}
            <div>
              <div className={cn('mb-2', classNames?.productPrice)}>
                <div className="flex items-baseline gap-2">
                  {product.availableForSale ? (
                    <>
                      <Heading size={2} className="text-info-primary" as="h6">
                        {product.price}
                      </Heading>
                      {showOriginalPrice && product.originalPrice && (
                        <Heading size={2} className="text-info-tertiary line-through" as="h6">
                          {product.originalPrice}
                        </Heading>
                      )}
                    </>
                  ) : (
                    <Heading size={2} className="text-info-tertiary">
                      {copy?.outOfStockLabel ?? 'Sold Out'}
                    </Heading>
                  )}
                </div>
              </div>

              {/* 按钮区域 */}
              <div className={cn('lg-desktop:gap-3 tablet:flex-nowrap flex flex-wrap gap-2', classNames?.buttonGroup)}>
                {secondaryButtonText && (
                  <Button
                    variant="secondary"
                    size="base"
                    className={cn('tablet:w-fit w-full', classNames?.secondaryButton)}
                    onClick={() => handleButtonClick(secondaryButtonFun, 'secondary')}
                    disabled={!product.availableForSale && secondaryButtonFun !== 'learnMore'}
                    loading={secondaryLoading}
                    data-headless-type-name="ActiveShelf#ProductCard"
                    data-headless-title-desc-button={`${product.custom_name || product.name}#${product.description || ''}#${secondaryButtonText}`}
                    data-headless-sku={product.sku}
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {primaryButtonText && (
                  <Button
                    variant="primary"
                    size="base"
                    className={cn('tablet:w-fit w-full', classNames?.primaryButton)}
                    onClick={() => handleButtonClick(primaryButtonFun, 'primary')}
                    disabled={!product.availableForSale && primaryButtonFun !== 'learnMore'}
                    loading={primaryLoading}
                    data-headless-type-name="ActiveShelf#ProductCard"
                    data-headless-title-desc-button={`${product.custom_name || product.name}#${product.description || ''}#${primaryButtonText}`}
                    data-headless-sku={product.sku}
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </div>

              {/* 库存信息 */}
              {shouldShowStock(
                stockDisplayMode,
                stockThresholdValue,
                product?.quantityAvailable ?? 0,
                product?.totalInventory ?? 0
              ) && (
                <div className="mt-4 space-y-2">
                  <Progress
                    value={stockPercentage}
                    max={100}
                    min={0}
                    size="base"
                    variant="default"
                    aria-label="stock progress"
                    classNames={{
                      root: cn('bg-[var(--progress-track-bg,rgba(0,0,0,0.2))]', classNames?.stockTrack),
                      progressBar: cn('bg-brand-0 transition-all duration-300 ease-in-out', classNames?.stockBar),
                    }}
                  />
                  <Text size={3} className="text-info-tertiary text-[14px]">
                    {overselling
                      ? copy?.limitedStock
                      : copy?.stockDisplayText?.replace(
                          '{count}',
                          `${product.availableForSale ? product.quantityAvailable : 0}`
                        )}
                  </Text>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }
)

ProductCard.displayName = 'ActiveShelf.ProductCard'

export { ProductCard }
export default ProductCard
